import { Paper, Table, TableBody, TableCell } from "@material-ui/core";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import clsx from "clsx";
import { t } from "i18next";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { useScrollContext } from "qpr-base/context/ScrollContextProvider";
import { getElementName } from "qpr-base/tplutils/utils";
import Loader from "qpr-base/ui/components/Loader";
import useQueryParams from "qpr-base/ui/hooks/useQueryParams";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import { Benchmark, SubKPIs } from "../KPIsForm/index.dto";
import { Milestones } from "../ProjectForm/index.dto";
import {
  getMeasurementDetails,
  useMeasurementUnitQueries,
} from "../components/MeasurementUnit";
import SimpleFormTable, { SimpleCell } from "./formTable";

const useListStyles = makeThemedStyles("", (theme) => ({
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #E6E6E6",
    marginTop: "16px",
  },
  table: {},
  row: {},
  nameContainer: {
    width: "30%",
    borderRight: "1px solid #E6E6E6",
    borderBottom: "1px solid #E6E6E6",
    fontSize: "16px",
  },
  descriptionContainer: {
    borderBottom: "1px solid #E6E6E6",
    fontSize: "16px",
  },
  label: {
    color: "#7E8791",
  },
}));
const SubInfoList = (props: {
  currentList: Milestones[][] | any;
  headers: string[];
  setField: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  openForm: boolean;
  editForm: React.Dispatch<React.SetStateAction<number | undefined>>;
  formName: string;
  disableButtons: boolean;
  projectDates: any[];
  isMileStone?: boolean;
  unit?: {
    ar: string;
    "en-gb": string;
  };
  setOpenForm: () => void;
  selectedGESymbol?: string;
  GovernmentEntities?: any;
  role?: string;
}) => {
  const classes = useListStyles();
  const ctx = useAppContext();

  const queryParams = useQueryParams();
  const { translationsLoading, unitData, unitDataTranslations, unitLoading } =
    useMeasurementUnitQueries(ctx.fullModelId);

  const scrollTo = useScrollContext((state) => state.scrollTo);

  const getMainKPIGE = () => {
    const GEMetadata =
      props.selectedGESymbol &&
      props.GovernmentEntities?.find(
        (ge: any) => ge.symbol === props.selectedGESymbol
      );
    return GEMetadata ? getElementName(GEMetadata, ctx.language) : "";
  };

  if (!props.currentList.length) {
    return <></>;
  }
  const deleteMilestone = (index: number) => {
    props.setField(
      props.formName,
      props.currentList
        .map((cur: any, ind: number) => ({
          ...cur,
          status:
            ind === index
              ? cur.meaId
                ? "delete"
                : "deleteFromList"
              : cur.status,
        }))
        .filter((cur: any) => cur.status !== "deleteFromList")
    );
  };
  const editMilestone = (index: number) => {
    if (props.openForm) {
      alert(
        t(
          `Please complete the current ${
            props.isMileStone
              ? "milestone"
              : props.formName === "Sub-KPIs"
              ? "sub-kpi"
              : "benchmark"
          } form to edit this one.`
        )
      );
    } else {
      props.setOpenForm();
      scrollTo(
        props.isMileStone
          ? "milestones"
          : props.formName === "Sub-KPIs"
          ? "subKpis"
          : "benchmark"
      );
      props.editForm(index);
    }
  };
  const headers: string[] = props.headers || [];
  const getHeaders = (startDate?: string, endDate?: string) => {
    // TODO
    return headers;

    /*

    if (props.isMileStone) {
      return startDate !== undefined && endDate !== undefined
        ? getAllQDatesBetween(
            getYearWithQuarter(startDate),
            getYearWithQuarter(endDate)
          ).map((qdate: QDate) => `Q${qdate.quarter + 1} / ${qdate.year}`)
        : headers;
    }
    return props.projectDates[0] !== undefined &&
      props.projectDates[1] !== undefined
      ? getAllQDatesBetween(
          getYearWithQuarter(props.projectDates[0]),
          getYearWithQuarter(props.projectDates[1])
        ).map((qdate: QDate) => `Q${qdate.quarter + 1} / ${qdate.year}`)
      : headers;
    */
  };

  if (translationsLoading || unitLoading) {
    return <Loader />;
  }

  const lang = conf.getLanguages();

  const getMilestoneValue = (
    valueObj: any,
    period: string | undefined,
    series: string
  ): number | undefined => {
    if (!valueObj || !period) return undefined;
    const value = valueObj[`${series}###${period}`];
    return value;
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={clsx(classes.nameContainer, classes.label)}>
              <div
                style={{
                  display: "flex",
                  justifyContent: ctx.rtl ? "right" : "left",
                }}
              >
                {t(`Current ${props.formName}`)}
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <div style={{ margin: "16px" }}>
            {props.currentList.map(
              (cell: Milestones | Benchmark | SubKPIs, index: number) => {
                if (!cell || cell.status === "delete") {
                  return <></>;
                }
                const milestoneCell = cell as Milestones;
                const benchmarkCell = cell as Benchmark;
                const subkpiCell = cell as SubKPIs;
                const benchmarkValues = benchmarkCell.values || [];
                let rows: {
                  milestoneProgress: SimpleCell[];
                  milestonePlannedProgress: SimpleCell[];
                  "Project Start Date": SimpleCell[];
                  "Project End Date": SimpleCell[];
                  milestoneWeight: SimpleCell[];
                } = {
                  "Project Start Date": [],
                  "Project End Date": [],
                  milestonePlannedProgress: [],
                  milestoneProgress: [],
                  milestoneWeight: [],
                };
                const milestoneHeaders = getHeaders(
                  milestoneCell["Milestone planned start date"],
                  milestoneCell["Milestone planned end date"]
                );
                const tempRows: any = {
                  milestoneProgress: milestoneHeaders.map((_) => ""),
                  milestonePlannedProgress: milestoneHeaders.map((_) => ""),
                  "Project Start Date": milestoneHeaders.map((_) => ""),
                  "Project End Date": milestoneHeaders.map((_) => ""),
                };
                const cells =
                  milestoneCell.milestoneValues &&
                  milestoneCell.milestoneValues.length
                    ? milestoneCell.milestoneValues.map((e: any) =>
                        e ? Object.entries(e) : []
                      )
                    : [];
                cells.forEach((c: any, ind: any) => {
                  if (ind >= milestoneHeaders.length) {
                    return;
                  }
                  c.forEach((e: any) => {
                    const [field, value] = [e[0], e[1]];
                    if (tempRows[field]) {
                      tempRows[field][ind] =
                        (field === "Project End Date" ||
                          field === "Project Start Date") &&
                        value
                          ? new Date(value)
                          : value;
                    }
                  });
                });
                rows.milestoneProgress = tempRows.milestoneProgress.map(
                  (e: any, index: number) =>
                    ({
                      name: "",
                      type: "numberField",
                      disabled: true,
                      value: getMilestoneValue(
                        milestoneCell.values,
                        milestoneHeaders[index],
                        "COMPLETION_PERCENTAGE"
                      ),
                      suffix: " %",
                    } as SimpleCell)
                );
                rows.milestonePlannedProgress =
                  tempRows.milestonePlannedProgress.map(
                    (e: any, index: number) =>
                      ({
                        name: "",
                        type: "numberField",
                        disabled: true,
                        value: getMilestoneValue(
                          milestoneCell.values,
                          milestoneHeaders[index],
                          "PLANNED_COMPLETION"
                        ),
                        suffix: " %",
                      } as SimpleCell)
                  );
                rows["Project Start Date"] = tempRows["Project Start Date"].map(
                  (e: any, index: number) =>
                    ({
                      name: "",
                      type: "dateField",
                      disabled: true,
                      value: getMilestoneValue(
                        milestoneCell.values,
                        milestoneHeaders[index],
                        "ACTUAL_START_DATE"
                      ),
                    } as SimpleCell)
                );
                rows["Project End Date"] = tempRows["Project End Date"].map(
                  (e: any, index: number) =>
                    ({
                      name: "",
                      type: "dateField",
                      disabled: true,
                      value: getMilestoneValue(
                        milestoneCell.values,
                        milestoneHeaders[index],
                        "ACTUAL_END_DATE"
                      ),
                    } as SimpleCell)
                );
                rows.milestoneWeight = tempRows.milestoneProgress.map(
                  (e: any) =>
                    ({
                      name: "",
                      type: "numberField",
                      disabled: true,
                      value: milestoneCell["Milestone weight"],
                      suffix: "%",
                    } as SimpleCell)
                );
                let name: string = "";
                let description: string = "";
                if (props.isMileStone) {
                  name =
                    milestoneCell["basicInformation"]?.[ctx.language]?.[
                      "Project Name"
                    ];

                  //  milestoneCell["Localized-Text-Attributes"]?.[
                  //    ctx.language
                  //  ]?.["Project Name"] || "";
                  description =
                    milestoneCell["basicInformation"]?.[ctx.language]?.[
                      "Milestone Description"
                    ];
                } else {
                  name = "undefined";
                  description = "undefined";
                }
                let unit: { "en-gb": string; ar: string } | undefined;
                if (subkpiCell.unit) {
                  const { unitTranslations } = getMeasurementDetails(
                    subkpiCell.unit,
                    unitData,
                    unitDataTranslations
                  );
                  if (unitTranslations) {
                    unit = {
                      ar: unitTranslations["Localized-Text-Attributes"].ar
                        .Symbol,
                      "en-gb":
                        unitTranslations["Localized-Text-Attributes"]["en-gb"]
                          .Symbol,
                    };
                  }
                }

                const disableEdit = () => {
                  if (props.disableButtons) return true;

                  const isEditView =
                    queryParams.get("type") === "existing" ||
                    queryParams.get("type") === "modify";
                  if (
                    props.role === "__WF_GE_COORDINATOR" &&
                    props.formName === "Sub-KPIs"
                  ) {
                    // Disable editing of existing subKPI in Add KPI -view
                    if (!isEditView && subkpiCell.id_of_original) return true;
                    // Disable editing of existing subKPI in Edit KPI -view, if owner is not same
                    if (
                      isEditView &&
                      subkpiCell.id_of_original &&
                      props.selectedGESymbol !== `GE_${subkpiCell["GE ID"]}`
                    )
                      return true;
                  }
                  return false;
                };

                const sameOwner = true;
                const parsedGEID =
                  subkpiCell["GE ID"] && JSON.parse(subkpiCell["GE ID"]);
                const owner =
                  parsedGEID &&
                  props.GovernmentEntities?.find(
                    (ge: any) =>
                      JSON.parse(ge.translations).ID === parsedGEID.toString()
                  );
                return (
                  <>
                    <TableRow key={`${index}-${props.formName}`}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexDirection: "row",
                        }}
                      >
                        <h1>{name}</h1>
                        <div>
                          <div
                            style={{
                              color: "green",
                              fontSize: "16px",
                              cursor: disableEdit() ? "not-allowed" : "pointer",
                              opacity: disableEdit() ? "0.5" : "1",
                            }}
                            onClick={() => {
                              if (!disableEdit()) {
                                editMilestone(index);
                              }
                            }}
                            role="button"
                            onKeyDown={() => {
                              if (!disableEdit() && sameOwner) {
                                editMilestone(index);
                              }
                            }}
                            tabIndex={0}
                          >
                            {t("EDIT")} {">"}
                          </div>
                          <div
                            style={{
                              color: "red",
                              fontSize: "16px",
                              textDecoration: "underline",
                              cursor: props.disableButtons
                                ? "not-allowed"
                                : "pointer",
                              opacity: props.disableButtons ? "0.5" : "1",
                            }}
                            onClick={() => {
                              if (!props.disableButtons) {
                                deleteMilestone(index);
                              }
                            }}
                            onKeyDown={() => {
                              if (!props.disableButtons) {
                                deleteMilestone(index);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            {t("DELETE")}
                          </div>
                        </div>
                      </div>
                    </TableRow>
                    <TableRow>
                      <h2>{description}</h2>
                    </TableRow>
                    {props.formName === "Sub-KPIs" && (
                      <TableRow>
                        <p style={{ fontSize: "16px" }}>
                          {t("Symbol")}: {subkpiCell.symbol}
                        </p>
                        <p style={{ fontSize: "16px" }}>
                          {t("forms.Measurement Unit")}:{" "}
                          {unit && unit[ctx.rtl ? "ar" : "en-gb"]}
                        </p>
                        <p style={{ fontSize: "16px" }}>
                          {t("Formula Reference")}:{" "}
                          {`DP2030_SUBKPIS.${subkpiCell.symbol}.ACT`}
                        </p>
                        <p style={{ fontSize: "16px" }}>
                          {t("GE Owner Name")} :{" "}
                          {`${
                            (owner && getElementName(owner, ctx.language)) ||
                            getMainKPIGE()
                          }`}
                        </p>
                      </TableRow>
                    )}
                    <TableRow>
                      <div style={{ marginTop: "16px", width: "73vw" }}>
                        <SimpleFormTable
                          disabled
                          headers={[t("VALUE NAME"), ...milestoneHeaders]}
                          rows={
                            props.isMileStone
                              ? [
                                  [
                                    t("Milestone Progress %"),
                                    ...rows.milestoneProgress,
                                  ],
                                  [
                                    t("Milestone Planned Progress %"),
                                    ...rows.milestonePlannedProgress,
                                  ],
                                  [
                                    t("Milestone Actual Start Date"),
                                    ...rows["Project Start Date"],
                                  ],
                                  [
                                    t("Milestone Actual End Date"),
                                    ...rows["Project End Date"],
                                  ],
                                ]
                              : [
                                  [
                                    t("Actual"),
                                    ...getHeaders(undefined, undefined).map(
                                      (year, ind) => {
                                        let cellUnit: {
                                          "en-gb": string;
                                          ar: string;
                                        } = unit ||
                                          props.unit || { "en-gb": "", ar: "" };
                                        return {
                                          type: "numberField",
                                          name: `${props.formName}[${index}].values[${ind}].ACT`,
                                          suffix:
                                            cellUnit[ctx.rtl ? "ar" : "en-gb"],
                                          value: Number.isNaN(
                                            benchmarkValues[ind]?.ACT
                                          )
                                            ? ""
                                            : benchmarkValues[ind]?.ACT,
                                        } as SimpleCell;
                                      }
                                    ),
                                  ],
                                ]
                          }
                        />
                      </div>
                    </TableRow>
                  </>
                );
              }
            )}
          </div>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SubInfoList;
